import { useState, useEffect, useCallback, useRef } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, RotateCcw, Heart, Star, Volume2, VolumeX, Key } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";
import { FieldScene3D } from "@/components/Scene3D";

import { getVoiceLang } from "@/utils/voicePref";
const LEVELS = [
  { id: 1, name: "Level 1", desc: "Straight path!", friction: 0.97, goalX: 88, obstacles: [], keys: 0, narrowPath: false },
  { id: 2, name: "Level 2", desc: "Uphill!", friction: 0.96, goalX: 88, obstacles: [{ type: "hill", x: 50, w: 20 }], keys: 0, narrowPath: false },
  { id: 3, name: "Level 3", desc: "Push the box!", friction: 0.96, goalX: 88, obstacles: [{ type: "box", x: 55, w: 6 }], keys: 0, narrowPath: false },
  { id: 4, name: "Level 4", desc: "Narrow path!", friction: 0.95, goalX: 88, obstacles: [{ type: "hill", x: 40, w: 15 }], keys: 0, narrowPath: true },
  { id: 5, name: "Level 5", desc: "Find 2 keys!", friction: 0.95, goalX: 88, obstacles: [{ type: "box", x: 45, w: 5 }], keys: 2, narrowPath: false },
];

const playPushSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 250;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.1);
  } catch (err) { logger.debug("game op failed", err); }
};

const playGoalSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523, 659, 784, 1047].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = f; osc.type = "sine";
      gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.25);
      osc.start(ctx.currentTime + i * 0.12); osc.stop(ctx.currentTime + i * 0.12 + 0.25);
    });
  } catch (err) { logger.debug("game op failed", err); }
};

const playKeySound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 880; osc.type = "sine";
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.2);
  } catch (err) { logger.debug("game op failed", err); }
};

const speakWin = (text) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0; u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const DongBola = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [currentLevel, setCurrentLevel] = useState(0);
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => {
    const saved = loadPlayerNames();
    return [saved[0] || "", saved[1] || ""];
  });
  const [ballX, setBallX] = useState(15);
  const [ballVX, setBallVX] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const [keysCollected, setKeysCollected] = useState(0);
  const [keyPositions, setKeyPositions] = useState([]);
  const [boxX, setBoxX] = useState(55);
  const [pushingLeft, setPushingLeft] = useState(false);
  const [pushingRight, setPushingRight] = useState(false);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);

  const level = LEVELS[currentLevel];
  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";

  const initLevel = useCallback(() => {
    setBallX(15);
    setBallVX(0);
    setKeysCollected(0);
    setPushingLeft(false);
    setPushingRight(false);
    const lvl = LEVELS[currentLevel];
    setBoxX(lvl.obstacles.find(o => o.type === "box")?.x || 55);
    if (lvl.keys > 0) {
      setKeyPositions(Array.from({ length: lvl.keys }, (_, i) => ({
        id: i, x: 30 + i * 25, collected: false,
      })));
    } else {
      setKeyPositions([]);
    }
  }, [currentLevel]);

  const startLevel = (lvlIdx) => {
    savePlayerNames(playerNames);
    setCurrentLevel(lvlIdx);
    setGameState("playing");
  };

  useEffect(() => {
    if (gameState === "playing") initLevel();
  }, [gameState, currentLevel, initLevel]);

  // Game loop
  useEffect(() => {
    if (gameState !== "playing") return;

    const gameLoop = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = Math.min((timestamp - lastTimeRef.current) / 16.67, 3);
      lastTimeRef.current = timestamp;

      setBallX(prev => {
        let newX = prev + ballVX * delta;
        if (newX < 5) newX = 5;

        // Box collision
        const boxObs = level.obstacles.find(o => o.type === "box");
        if (boxObs) {
          const bxLeft = boxX - boxObs.w / 2;
          const bxRight = boxX + boxObs.w / 2;
          if (newX + 3 > bxLeft && newX - 3 < bxRight && ballVX > 0) {
            setBoxX(bx => {
              const newBx = bx + ballVX * delta * 0.8;
              return Math.min(newBx, 95);
            });
          }
        }

        // Hill slowdown
        const hillObs = level.obstacles.find(o => o.type === "hill");
        if (hillObs && newX > hillObs.x - hillObs.w / 2 && newX < hillObs.x + hillObs.w / 2) {
          setBallVX(v => v * 0.95);
        }

        // Key collection
        if (level.keys > 0) {
          setKeyPositions(prev => prev.map(k => {
            if (k.collected) return k;
            if (Math.abs(newX - k.x) < 5) {
              setKeysCollected(c => c + 1);
              if (soundOn) playKeySound();
              return { ...k, collected: true };
            }
            return k;
          }));
        }

        // Goal check
        if (newX >= level.goalX) {
          if (level.keys > 0 && keysCollected < level.keys) {
            return level.goalX - 1;
          }
          setGameState("won");
          recordGameWon("dongbola");
          if (soundOn) {
            playGoalSound();
            setTimeout(() => speakWin(`Great! ${p1} and ${p2} did it!`), 500);
          }
          return newX;
        }

        return newX;
      });

      setBallVX(prev => prev * level.friction);

      rafRef.current = requestAnimationFrame(gameLoop);
    };

    rafRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTimeRef.current = 0;
    };
  }, [gameState, level, ballVX, keysCollected, soundOn, boxX, p1, p2]);

  // Co-op mechanic: both players must push together for full force
  const [p1Pushing, setP1Pushing] = useState(false);
  const [p2Pushing, setP2Pushing] = useState(false);
  const p1Timer = useRef(null);
  const p2Timer = useRef(null);

  const applyPush = useCallback((who) => {
    if (gameState !== "playing") return;
    if (soundOn) playPushSound();

    if (who === 1) {
      setP1Pushing(true);
      setPushingLeft(true);
      clearTimeout(p1Timer.current);
      p1Timer.current = setTimeout(() => { setP1Pushing(false); setPushingLeft(false); }, 400);
    } else {
      setP2Pushing(true);
      setPushingRight(true);
      clearTimeout(p2Timer.current);
      p2Timer.current = setTimeout(() => { setP2Pushing(false); setPushingRight(false); }, 400);
    }
  }, [gameState, soundOn]);

  // Apply force based on who is pushing
  useEffect(() => {
    if (gameState !== "playing") return;
    if (p1Pushing && p2Pushing) {
      // Both pushing = full force!
      setBallVX(prev => prev + 1.2);
    } else if (p1Pushing || p2Pushing) {
      // Only one pushing = very weak
      setBallVX(prev => prev + 0.15);
    }
  }, [p1Pushing, p2Pushing, gameState]);

  // Keyboard controls
  useEffect(() => {
    if (gameState !== "playing") return;
    const handleDown = (e) => {
      const k = e.key.toLowerCase();
      if (k === 'a' || k === 'd') applyPush(1);
      if (k === 'j' || k === 'l') applyPush(2);
    };
    window.addEventListener("keydown", handleDown);
    return () => window.removeEventListener("keydown", handleDown);
  }, [gameState, applyPush]);

  const pushMobile = (who) => {
    applyPush(who);
  };

  // MENU
  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="dorong-bola-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ x: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            ⚽
          </motion.span>
          <h1 className="font-fredoka text-3xl sm:text-4xl text-gold font-bold mb-2">Dorong Bola ke Rumah</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-4">
            Kerja sama dorong bola sampai masuk ke rumah!
          </p>
          <div className="glass-card rounded-xl p-3 mb-4 text-left">
            <p className="font-fredoka text-gold-light/70 text-xs mb-1 font-bold">Cara Bermain:</p>
            <p className="font-fredoka text-gold-light/60 text-xs">{p1}: Tekan <span className="text-cyan-400 font-bold">A/D</span> | {p2}: Tekan <span className="text-pink-400 font-bold">J/L</span></p>
            <p className="font-fredoka text-gold-light/60 text-xs">Mobile: Tap tombol dorong di bawah layar</p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="font-fredoka text-cyan-400 text-xs font-bold block mb-1">{`Pemain 1 (A/D)`}</label>
              <input data-testid="dorong-p1-name" type="text" value={playerNames[0]}
                onChange={(e) => setPlayerNames(prev => [e.target.value, prev[1]])}
                placeholder="Pemain 1" maxLength={15}
                className="w-full glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-cyan-400/50" />
            </div>
            <div>
              <label className="font-fredoka text-pink-400 text-xs font-bold block mb-1">{`Pemain 2 (J/L)`}</label>
              <input data-testid="dorong-p2-name" type="text" value={playerNames[1]}
                onChange={(e) => setPlayerNames(prev => [prev[0], e.target.value])}
                placeholder="Pemain 2" maxLength={15}
                className="w-full glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-pink-400/50" />
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-2 w-full mb-4">
          {LEVELS.map((lvl, idx) => (
            <motion.div key={lvl.id} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: idx * 0.1 }}>
              <Button data-testid={`dorong-level-${lvl.id}`} onClick={() => startLevel(idx)}
                className="w-full h-14 font-fredoka font-bold text-base rounded-xl justify-between px-4"
                style={{ background: `linear-gradient(135deg, hsl(${30 + idx * 25}, 70%, 50%), hsl(${30 + idx * 25}, 70%, 40%))` }}>
                <span className="flex items-center gap-2"><span className="text-xl">⚽</span>{lvl.name}</span>
                <span className="text-xs opacity-80">{lvl.desc}</span>
              </Button>
            </motion.div>
          ))}
        </div>

        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl">
          <Home className="w-4 h-4 mr-2" />Kembali ke Menu Utama
        </Button>
      </div>
    );
  }

  // WIN
  if (gameState === "won") {
    const isLastLevel = currentLevel >= LEVELS.length - 1;
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="dorong-bola-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={300}
          colors={["#FFD700", "#FF6347", "#4ECDC4", "#FF69B4", "#87CEEB"]} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
          className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <motion.span className="text-6xl block mb-3" animate={{ y: [0, -15, 0] }} transition={{ duration: 0.6, repeat: 3 }}>
            🏠
          </motion.span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Bola Berhasil Pulang!</h2>
          <p className="font-fredoka text-gold-light/80 mb-1">{p1} & {p2} hebat!</p>
          <div className="flex justify-center gap-1 mb-4">
            {[0, 1, 2].map(i => <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />)}
          </div>
          <div className="flex flex-col gap-2">
            {!isLastLevel && (
              <Button onClick={() => { setCurrentLevel(prev => prev + 1); setGameState("playing"); }}
                className="w-full font-fredoka font-bold text-base rounded-xl h-12 bg-gradient-to-r from-green-400 to-emerald-500"
                data-testid="dorong-next-level">
                Level Berikutnya
              </Button>
            )}
            <Button onClick={() => setGameState("playing")}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"
              data-testid="dorong-play-again">
              <RotateCcw className="w-4 h-4 mr-2" />Main Lagi
            </Button>
            <Button onClick={() => setGameState("menu")} variant="outline"
              className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl">
              Pilih Level
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // PLAYING
  const goalX = level.goalX;
  const hillObs = level.obstacles.find(o => o.type === "hill");
  const boxObs = level.obstacles.find(o => o.type === "box");
  const groundY = level.narrowPath ? 65 : 70;

  return (
    <div className="flex flex-col items-center w-full max-w-3xl mx-auto px-2" data-testid="dorong-bola-game">
      {/* Header */}
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">⚽ {LEVELS[currentLevel].name}</span>
          {level.keys > 0 && (
            <div className="flex items-center gap-1">
              <Key className="w-4 h-4 text-yellow-400" />
              <span className="font-fredoka text-gold text-xs font-bold">{keysCollected}/{level.keys}</span>
            </div>
          )}
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-7 w-7 p-0">
            {soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
          </Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-7 px-2">
            <Home className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Game Area - 3D Side View */}
      <div className="w-full rounded-2xl overflow-hidden relative border-2 border-green-400/30 game-area-3d"
        style={{
          height: Math.min(winH - 180, 400),
          background: "linear-gradient(180deg, #87CEEB 0%, #B0E0E6 50%, #90EE90 75%, #228B22 100%)",
        }}>
        {/* Three.js 3D Background */}
        <FieldScene3D />

        {/* Sky decorations - animated */}
        <motion.div className="absolute top-[5%] text-3xl opacity-40 select-none"
          animate={{ x: [0, 20, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          style={{ left: '15%' }}>☁️</motion.div>
        <motion.div className="absolute top-[8%] text-2xl opacity-30 select-none"
          animate={{ x: [0, -15, 0] }} transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          style={{ right: '20%' }}>☁️</motion.div>
        <motion.div className="absolute top-[3%] text-4xl opacity-25 select-none"
          animate={{ rotate: [0, 5, 0] }} transition={{ duration: 8, repeat: Infinity }}
          style={{ left: '60%' }}>☀️</motion.div>

        {/* Birds */}
        <motion.div className="absolute top-[12%] text-xl select-none opacity-40"
          animate={{ x: [0, 60, 120], y: [0, -5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{ left: '5%' }}>🐦</motion.div>

        {/* Butterflies near flowers */}
        <motion.div className="absolute text-lg select-none opacity-60 z-[6]"
          animate={{ x: [0, 15, -10, 0], y: [0, -10, -5, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
          style={{ left: '25%', top: `${groundY - 2}%` }}>🦋</motion.div>

        {/* Ground */}
        <div className="absolute left-0 right-0 bg-gradient-to-t from-green-800 to-green-500"
          style={{ top: `${groundY + 8}%`, bottom: 0 }} />
        {/* Grass line */}
        <div className="absolute left-0 right-0 h-2"
          style={{ top: `${groundY + 7}%`, background: "linear-gradient(90deg, #228B22, #32CD32, #228B22)" }} />

        {/* Path/road */}
        <div className="absolute left-0 right-0"
          style={{
            top: `${groundY - (level.narrowPath ? 2 : 5)}%`,
            height: level.narrowPath ? '14%' : '18%',
            background: 'rgba(210,180,140,0.6)',
            borderTop: '2px solid rgba(139,119,101,0.5)',
            borderBottom: '2px solid rgba(139,119,101,0.5)',
          }} />

        {/* Hill obstacle */}
        {hillObs && (
          <div className="absolute select-none"
            style={{
              left: `${hillObs.x - hillObs.w / 2}%`,
              width: `${hillObs.w}%`,
              top: `${groundY - 8}%`,
              height: '15%',
              background: 'linear-gradient(180deg, #8B7355 0%, #A0522D 100%)',
              borderRadius: '50% 50% 0 0',
              opacity: 0.7,
            }} />
        )}

        {/* Box obstacle */}
        {boxObs && (
          <div className="absolute select-none flex items-center justify-center"
            style={{
              left: `${boxX - boxObs.w / 2}%`,
              width: `${boxObs.w}%`,
              top: `${groundY - 5}%`,
              height: '10%',
              background: 'linear-gradient(180deg, #D2691E, #8B4513)',
              border: '2px solid #5C3317',
              borderRadius: '4px',
            }}>
            <span className="text-lg">📦</span>
          </div>
        )}

        {/* Keys */}
        {keyPositions.map(k => !k.collected && (
          <motion.div key={k.id} className="absolute text-2xl z-[6] select-none"
            style={{ left: `${k.x}%`, top: `${groundY - 8}%` }}
            animate={{ y: [0, -5, 0], rotate: [0, 10, -10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}>
            🔑
          </motion.div>
        ))}

        {/* Goal house - breathing animation */}
        <motion.div className="absolute select-none flex flex-col items-center"
          style={{ left: `${goalX - 2}%`, top: `${groundY - 18}%` }}
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 2, repeat: Infinity }}>
          <span className="text-5xl" style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))" }}>🏠</span>
          {level.keys > 0 && keysCollected < level.keys && (
            <motion.span className="text-xs font-fredoka text-red-400 font-bold mt-0.5"
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: Infinity }}>🔒</motion.span>
          )}
        </motion.div>
        {/* Smoke from chimney */}
        <motion.div className="absolute text-lg select-none opacity-30"
          style={{ left: `${goalX}%`, top: `${groundY - 22}%` }}
          animate={{ y: [0, -10, -20], opacity: [0.3, 0.15, 0], scale: [1, 1.3, 1.5] }}
          transition={{ duration: 3, repeat: Infinity }}>💨</motion.div>

        {/* Player 1 character (left of ball) - 3D enhanced */}
        <motion.div className="absolute z-[8] select-none text-4xl char-3d"
          style={{ left: `${ballX - 6}%`, top: `${groundY - 6}%` }}
          animate={pushingLeft
            ? { x: [0, 8, 0], scaleX: [1, 1.15, 1], y: [0, -3, 0] }
            : { y: [0, -1.5, 0] }}
          transition={pushingLeft ? { duration: 0.2 } : { duration: 1.5, repeat: Infinity }}>
          👧
        </motion.div>

        {/* Ball - 3D enhanced with rotation and dust */}
        <motion.div className="absolute z-[9] select-none emoji-3d"
          style={{
            left: `${ballX}%`,
            top: `${groundY - 7}%`,
            transform: "translateX(-50%)",
          }}>
          <motion.span className="text-6xl block"
            animate={{ rotate: ballVX * 40 }}
            style={{
              filter: `drop-shadow(0 3px 6px rgba(0,0,0,0.3))${Math.abs(ballVX) > 0.3 ? ' brightness(1.1)' : ''}`,
            }}>
            ⚽
          </motion.span>
        </motion.div>
        {/* Dust particles when ball moves fast */}
        {Math.abs(ballVX) > 0.5 && (
          <motion.div className="absolute z-[7] select-none"
            style={{ left: `${ballX - 3}%`, top: `${groundY + 1}%` }}>
            {[0, 1, 2].map(i => (
              <motion.span key={`dust-${i}`} className="absolute text-xs opacity-40"
                style={{ left: `${-i * 3}%` }}
                animate={{ opacity: [0.4, 0], y: [-2, 5], x: [-5 * i, -10 * i] }}
                transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity }}>
                🟤
              </motion.span>
            ))}
          </motion.div>
        )}

        {/* Player 2 character (right of ball) - 3D enhanced */}
        <motion.div className="absolute z-[8] select-none text-4xl char-3d"
          style={{ left: `${ballX + 4}%`, top: `${groundY - 6}%`, transform: "scaleX(-1)" }}
          animate={pushingRight
            ? { x: [0, -8, 0], scaleX: [1, 1.15, 1], y: [0, -3, 0] }
            : { y: [0, -1.5, 0] }}
          transition={pushingRight ? { duration: 0.2 } : { duration: 1.5, repeat: Infinity, delay: 0.5 }}>
          👧
        </motion.div>

        {/* Co-op indicator */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20">
          <div className={`font-fredoka font-bold text-xs px-3 py-1 rounded-full ${p1Pushing && p2Pushing ? 'bg-green-500 text-white' : p1Pushing || p2Pushing ? 'bg-yellow-500 text-black' : 'bg-gray-500/50 text-white/70'}`}>
            {p1Pushing && p2Pushing ? 'KOMPAK! BOLA MAJU!' : p1Pushing || p2Pushing ? 'Dorong berdua!' : 'Tekan tombol berdua!'}
          </div>
        </div>

        {/* Start flag - waving */}
        <motion.div className="absolute text-2xl select-none" style={{ left: '3%', top: `${groundY - 8}%` }}
          animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}>🚩</motion.div>

        {/* Flowers on grass */}
        <motion.div className="absolute text-lg select-none opacity-70" style={{ left: '15%', top: `${groundY + 6}%` }}
          animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 3, repeat: Infinity }}>🌷</motion.div>
        <motion.div className="absolute text-base select-none opacity-60" style={{ left: '35%', top: `${groundY + 7}%` }}
          animate={{ rotate: [0, -4, 4, 0] }} transition={{ duration: 3.5, repeat: Infinity }}>🌻</motion.div>
        <motion.div className="absolute text-lg select-none opacity-70" style={{ left: '70%', top: `${groundY + 6}%` }}
          animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 2.5, repeat: Infinity }}>🌸</motion.div>
      </div>

      {/* Mobile Controls */}
      <div className="flex justify-between w-full mt-3 gap-4">
        <Button data-testid="dorong-push-left" onClick={() => pushMobile(1)}
          className="flex-1 h-16 sm:h-20 text-base sm:text-lg font-fredoka font-bold rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 btn-3d select-none"
          style={{ touchAction: "manipulation" }}>
          👧 {p1} DORONG!
        </Button>
        <Button data-testid="dorong-push-right" onClick={() => pushMobile(2)}
          className="flex-1 h-16 sm:h-20 text-base sm:text-lg font-fredoka font-bold rounded-2xl bg-gradient-to-r from-orange-400 to-red-500 btn-3d select-none"
          style={{ touchAction: "manipulation" }}>
          {p2} DORONG! 👧
        </Button>
      </div>
    </div>
  );
};

export default DongBola;
